/**
 * Platform Fees - Plattform-spezifische Gebühren
 */

import type { CalculatorInput } from '@/types/price-calculator';
import { PLATFORM_PRESETS } from '@/lib/price-calculator';
import { cn } from '@/lib/utils';
import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface PlatformFeesProps {
  input: CalculatorInput;
  onUpdate: <K extends keyof CalculatorInput>(field: K, value: CalculatorInput[K]) => void;
}

function NumberInput({
  label,
  value,
  onChange,
  suffix = '€',
  disabled = false,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  suffix?: string;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">{label}</label>
      <div className="relative">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          disabled={disabled}
          step={0.01}
          className={cn(
            'w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600',
            'bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100',
            'focus:ring-2 focus:ring-primary-500 focus:border-transparent',
            'text-right pr-8',
            disabled && 'opacity-60 cursor-not-allowed'
          )}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 text-sm">
          {suffix}
        </span>
      </div>
    </div>
  );
}

export function PlatformFees({ input, onUpdate }: PlatformFeesProps) {
  const preset = PLATFORM_PRESETS[input.platformId];
  const isCustom = input.platformId === 'custom';
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
        {t('pricing.platformFees', { name: preset.name })}
      </h3>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <NumberInput
          label={t('pricing.commission')}
          value={isCustom ? input.customCommission : preset.commissionPercent}
          onChange={(v) => onUpdate('customCommission', v)}
          suffix="%"
          disabled={!isCustom}
        />
        <NumberInput
          label={t('pricing.fixedFee')}
          value={isCustom ? input.customFixedFee : preset.fixedFee}
          onChange={(v) => onUpdate('customFixedFee', v)}
          disabled={!isCustom}
        />
        <NumberInput
          label={t('pricing.paymentFee')}
          value={preset.paymentFeePercent}
          onChange={() => {}}
          suffix="%"
          disabled
        />
      </div>

      {/* FBA Options */}
      {preset.hasFBA && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={input.useFBA}
              onChange={(e) => onUpdate('useFBA', e.target.checked)}
              className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="font-medium text-neutral-900 dark:text-neutral-100">
              {t('pricing.fbaActivate')}
            </span>
          </label>

          {input.useFBA && (
            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <NumberInput
                  label={t('pricing.productWeight')}
                  value={input.productWeight}
                  onChange={(v) => onUpdate('productWeight', v)}
                  suffix="kg"
                />
                <NumberInput
                  label={t('pricing.storageCostsMonth')}
                  value={input.fbaStorageCost}
                  onChange={(v) => onUpdate('fbaStorageCost', v)}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={input.fbaGermanyOnly}
                    onChange={(e) => onUpdate('fbaGermanyOnly', e.target.checked)}
                    className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">
                    {t('pricing.fbaGermanyOnly')}
                  </span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={input.fbaPeakSeason}
                    onChange={(e) => onUpdate('fbaPeakSeason', e.target.checked)}
                    className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-neutral-700 dark:text-neutral-300">
                    {t('pricing.fbaPeakSeason')}
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notes */}
      {preset.notes.length > 0 && (
        <div className="flex gap-2 p-3 bg-neutral-100 dark:bg-neutral-800 rounded-lg">
          <Info className="h-4 w-4 text-neutral-500 flex-shrink-0 mt-0.5" />
          <ul className="text-xs text-neutral-600 dark:text-neutral-400 space-y-1">
            {preset.notes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
